/**
 * Test específico para notificaciones de sesión por inactividad.
 * Uso desde consola: await window.__testSesionInactividad()
 */

import { notifyDesktopViaServiceWorkerOrFallback } from './webPush';

function detectBrowser() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent.toLowerCase();
  
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('chrome')) return 'chrome';
  if (ua.includes('opera') || ua.includes('opr/')) return 'opera';
  
  return 'unknown';
}

async function requestNotificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  
  try {
    // API moderna (Promise-based) - Chrome, Firefox, Edge moderno
    if (typeof Notification.requestPermission === 'function') {
      const result = await Notification.requestPermission();
      return result;
    }
    
    // API legacy (callback-based) - Safari, navegadores antiguos
    return new Promise((resolve) => {
      Notification.requestPermission((result) => {
        resolve(result);
      });
    });
  } catch (err) {
    console.warn('Error solicitando permiso:', err);
    return 'denied';
  }
}

export async function testSessionNotifications() {
  const browser = detectBrowser();
  console.group(`[SessionTest] Prueba de notificaciones de inactividad (${browser})`);
  
  // 1. Verificar estado básico
  console.log('Navegador detectado:', browser);
  console.log('Notification.permission:', typeof Notification !== 'undefined' ? Notification.permission : 'N/A');
  console.log('ServiceWorker disponible:', 'serviceWorker' in navigator);
  console.log('Pestaña visible:', typeof document !== 'undefined' ? document.visibilityState : 'N/A');
  
  if (typeof Notification === 'undefined') {
    console.error('❌ API Notification no disponible en este navegador');
    console.groupEnd();
    return false;
  }
  
  if (Notification.permission !== 'granted') {
    console.warn('⚠️ Permiso no concedido. Solicitando...');
    const perm = await requestNotificationPermission();
    console.log('Resultado:', perm);
    if (perm !== 'granted') {
      console.error('❌ Sin permiso, no se pueden mostrar notificaciones');
      console.log('\n🔧 Cómo habilitar notificaciones:');
      switch (browser) {
        case 'chrome':
        case 'edge':
          console.log('- Clic en el candado 🔒 junto a la URL → Notificaciones → Permitir');
          console.log('- O ir a chrome://settings/content/notifications');
          break;
        case 'firefox':
          console.log('- Clic en el escudo 🛡️ junto a la URL → Notificaciones → Permitir');
          console.log('- O ir a about:preferences#privacy → Notificaciones');
          break;
        case 'safari':
          console.log('- Safari → Preferencias → Sitios web → Notificaciones');
          break;
        default:
          console.log('- Buscar configuración de notificaciones en el navegador');
      }
      console.groupEnd();
      return false;
    }
  }
  
  // 2. Test de notificación de "sesión por expirar" (primer aviso)
  console.log('\n🔔 Enviando notificación de "sesión por expirar"...');
  const result1 = await notifyDesktopViaServiceWorkerOrFallback(
    'Sesión por expirar',
    'Sin actividad en la web la sesión se cerrará. Interactúa con la aplicación para seguir conectado.',
    { 
      tag: 'hs-session-warning-test', 
      renotify: true, 
      requireInteraction: true, 
      url: '/',
      inPageToastWhenVisible: false  // Solo notificación del SO, sin toast
    }
  );
  console.log('Resultado 1:', result1 ? '✅ enviado' : '❌ falló');
  
  // 3. Esperar 3 segundos y enviar notificación de "1 minuto restante"
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  console.log('\n🔔 Enviando notificación de "1 minuto restante"...');
  const result2 = await notifyDesktopViaServiceWorkerOrFallback(
    'Sesión por expirar',
    'La sesión se cerrará por inactividad en 1 minuto.',
    { 
      tag: 'hs-session-inactivity-1m-test', 
      renotify: true, 
      requireInteraction: true, 
      url: '/',
      inPageToastWhenVisible: false
    }
  );
  console.log('Resultado 2:', result2 ? '✅ enviado' : '❌ falló');
  
  // 4. Esperar 3 segundos y enviar notificación de "sesión cerrada"
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  console.log('\n🔔 Enviando notificación de "sesión cerrada"...');
  const result3 = await notifyDesktopViaServiceWorkerOrFallback(
    'Sesión cerrada', 
    'Sesión finalizada por inactividad de 3 minutos',
    {
      tag: `hs-session-logout-test-${Date.now()}`,
      renotify: true,
      requireInteraction: true,
      silent: false,
      url: '/',
      inPageToastWhenVisible: false
    }
  );
  console.log('Resultado 3:', result3 ? '✅ enviado' : '❌ falló');
  
  const allOk = result1 && result2 && result3;
  console.log(`\n📊 Resumen: ${allOk ? '✅ Todas las notificaciones funcionan' : '❌ Algunas notificaciones fallaron'}`);
  
  if (!allOk) {
    console.log('\n🔧 Posibles causas:');
    console.log('- Configuración de Windows: Configuración → Sistema → Notificaciones');
    console.log('- Chrome: chrome://settings/content/notifications');
    console.log('- Asistente para el enfoque activado (suprime notificaciones)');
    console.log('- Service Worker no activo (ejecutar primero: await window.__testNotificacion())');
  }
  
  console.groupEnd();
  return allOk;
}

/**
 * Simula el flujo completo de inactividad de sesión (sin cerrar realmente la sesión).
 */
export async function simulateSessionInactivityFlow() {
  console.group('[SessionTest] Simulación completa de flujo de inactividad');
  
  console.log('⏱️ Simulando: usuario inactivo por 1 minuto...');
  console.log('🔔 Primer aviso: "Sesión por expirar"');
  
  await testSessionNotifications();
  
  console.log('\n✅ Simulación completada. En el flujo real:');
  console.log('1. Tras 1 min de inactividad → primer aviso');
  console.log('2. Tras 2 min de inactividad → "1 minuto restante"');  
  console.log('3. Tras 3 min de inactividad → "sesión cerrada" + logout automático');
  
  console.groupEnd();
}